export type PhoneCountryCode = 'GB' | 'US' | 'IE';
export type CreatorCurrencyCode = 'gbp' | 'usd' | 'eur';

export interface PhoneCountryPreset {
  countryCode: PhoneCountryCode;
  label: string;
  prefix: string;
  defaultCurrency: CreatorCurrencyCode;
}

export const PHONE_COUNTRY_PRESETS: readonly PhoneCountryPreset[] = [
  { countryCode: 'GB', label: 'United Kingdom (+44)', prefix: '+44', defaultCurrency: 'gbp' },
  { countryCode: 'US', label: 'United States (+1)', prefix: '+1', defaultCurrency: 'usd' },
  { countryCode: 'IE', label: 'Ireland (+353)', prefix: '+353', defaultCurrency: 'eur' },
] as const;

export const CURRENCY_OPTIONS: readonly { code: CreatorCurrencyCode; label: string }[] = [
  { code: 'gbp', label: 'GBP' },
  { code: 'usd', label: 'USD' },
  { code: 'eur', label: 'EUR' },
] as const;

export interface AdminRegisterCreatorRequest {
  blazeFirebaseUid: string;
  email: string;
  firstName: string;
  lastName: string;
  mobile: string;
  countryCode: PhoneCountryCode;
  currencyCode: CreatorCurrencyCode;
}

export interface AdminRegisterCreatorResponse {
  creator: { blazeFirebaseUid: string; email: string; firstName: string; lastName: string };
  created: boolean;
  firebaseEmail?: string;
}

export interface CreatorLookupSnapshot {
  email: string;
  firstName: string;
  lastName: string;
  mobile: string;
  countryCode: string;
  currencyCode: string;
}

export interface FirebaseCreatorLookupResponse {
  exists: boolean;
  email?: string;
  displayName?: string;
  phoneNumber?: string;
  creatorExists: boolean;
  creator?: CreatorLookupSnapshot;
}

export interface CreatorRegistrationFormModel {
  blazeFirebaseUid: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneCountry: PhoneCountryCode;
  phoneLocal: string;
  currencyCode: CreatorCurrencyCode;
}

const PHONE_RULES: Record<PhoneCountryCode, { min: number; max: number }> = {
  GB: { min: 10, max: 10 },
  US: { min: 10, max: 10 },
  IE: { min: 8, max: 9 },
};

const NAME_PATTERN = /^[\p{L}\s'-]+$/u;

export function getPhoneCountryPreset(countryCode: PhoneCountryCode): PhoneCountryPreset {
  return PHONE_COUNTRY_PRESETS.find((preset) => preset.countryCode === countryCode) ?? PHONE_COUNTRY_PRESETS[0];
}

export function buildMobileE164(countryCode: PhoneCountryCode, phoneLocal: string): string {
  const preset = getPhoneCountryPreset(countryCode);
  return `${preset.prefix}${phoneLocal.trim()}`;
}

export function normalizePhoneCountryCode(value?: string): PhoneCountryCode | null {
  const upper = value?.trim().toUpperCase();
  if (upper === 'GB' || upper === 'US' || upper === 'IE') {
    return upper;
  }
  return null;
}

export function normalizeCurrencyCode(value?: string): CreatorCurrencyCode | null {
  const lower = value?.trim().toLowerCase();
  if (lower === 'gbp' || lower === 'usd' || lower === 'eur') {
    return lower;
  }
  return null;
}

export function parseMobileToFormFields(
  mobile: string,
  countryCodeHint?: string,
): { phoneCountry: PhoneCountryCode; phoneLocal: string } | null {
  const trimmed = mobile?.trim();
  if (!trimmed) {
    return null;
  }

  const hintedCountry = normalizePhoneCountryCode(countryCodeHint);
  if (hintedCountry) {
    const preset = getPhoneCountryPreset(hintedCountry);
    if (trimmed.startsWith(preset.prefix)) {
      return {
        phoneCountry: preset.countryCode,
        phoneLocal: trimmed.slice(preset.prefix.length),
      };
    }
  }

  const presetsByPrefixLength = [...PHONE_COUNTRY_PRESETS].sort(
    (a, b) => b.prefix.length - a.prefix.length,
  );
  for (const preset of presetsByPrefixLength) {
    if (trimmed.startsWith(preset.prefix)) {
      return {
        phoneCountry: preset.countryCode,
        phoneLocal: trimmed.slice(preset.prefix.length),
      };
    }
  }

  return null;
}

export type CreatorRegistrationSnapshot = {
  blazeFirebaseUid: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneCountry: PhoneCountryCode;
  phoneLocal: string;
  currencyCode: CreatorCurrencyCode;
};

export function toRegistrationSnapshot(
  model: CreatorRegistrationFormModel,
): CreatorRegistrationSnapshot {
  return {
    blazeFirebaseUid: model.blazeFirebaseUid.trim(),
    email: model.email.trim(),
    firstName: model.firstName.trim(),
    lastName: model.lastName.trim(),
    phoneCountry: model.phoneCountry,
    phoneLocal: model.phoneLocal.trim(),
    currencyCode: model.currencyCode,
  };
}

export function registrationSnapshotsEqual(
  a: CreatorRegistrationSnapshot,
  b: CreatorRegistrationSnapshot,
): boolean {
  return (
    a.blazeFirebaseUid === b.blazeFirebaseUid &&
    a.email === b.email &&
    a.firstName === b.firstName &&
    a.lastName === b.lastName &&
    a.phoneCountry === b.phoneCountry &&
    a.phoneLocal === b.phoneLocal &&
    a.currencyCode === b.currencyCode
  );
}

export function applyCreatorLookupToRegistrationModel(
  current: CreatorRegistrationFormModel,
  lookup: FirebaseCreatorLookupResponse,
): CreatorRegistrationFormModel {
  const next: CreatorRegistrationFormModel = { ...current };

  if (lookup.creator) {
    next.email = lookup.creator.email || next.email;
    next.firstName = lookup.creator.firstName ?? '';
    next.lastName = lookup.creator.lastName ?? '';

    const country = normalizePhoneCountryCode(lookup.creator.countryCode);
    if (country) {
      next.phoneCountry = country;
    }

    const currency = normalizeCurrencyCode(lookup.creator.currencyCode);
    if (currency) {
      next.currencyCode = currency;
    }

    const phoneFields = parseMobileToFormFields(lookup.creator.mobile, lookup.creator.countryCode);
    if (phoneFields) {
      next.phoneCountry = phoneFields.phoneCountry;
      next.phoneLocal = phoneFields.phoneLocal;
    }

    return next;
  }

  if (lookup.email) {
    next.email = lookup.email;
  }

  return next;
}

export function validateCreatorName(value: string, fieldLabel: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return `${fieldLabel} is required`;
  }
  if (!NAME_PATTERN.test(trimmed)) {
    return `${fieldLabel} may only contain letters, spaces, hyphens, and apostrophes`;
  }
  return null;
}

export function validatePhoneLocal(countryCode: PhoneCountryCode, local: string): string | null {
  const trimmed = local.trim();
  if (!trimmed) {
    return 'Phone number is required';
  }
  if (trimmed.startsWith('0')) {
    return 'Enter the number without a leading 0';
  }
  if (!/^\d+$/.test(trimmed)) {
    return 'Phone number must contain digits only';
  }
  const rules = PHONE_RULES[countryCode];
  if (trimmed.length < rules.min || trimmed.length > rules.max) {
    return `Enter a valid mobile number (${rules.min}–${rules.max} digits)`;
  }
  return null;
}
